






















import Vue from 'vue';
import {Component, Emit, Prop, Watch} from "vue-property-decorator";
import AsiBtn from "../common/AsiBtn.vue";
import DailyPlanningData from "@/models/DailyPlanningData";
import {Icon} from "@/helpers/icons";
import AsiCard from "@/components/common/AsiCard.vue";
import UserSimple from "@/models/user/UserSimple";
import PlanningEntryCreateDialog from "@/components/planningEntry/PlanningEntryCreateDialog.vue";
import PlanningEntryListEntry from "@/models/planningEntry/PlanningEntryListEntry";
import {SnackbarMessage} from "@/models/SnackbarMessage";
import OrderSimple from "@/models/order/OrderSimple";
import PlanningEntryCreate from "@/models/planningEntry/PlanningEntryCreate";
import {toISODateString} from "@/helpers/functions_asi";
import DailyOverviewEntry from "@/components/planning/DailyOverviewEntry.vue";
import {Permissions} from "@/helpers/constants";

@Component({
	components: {
		DailyOverviewEntry, PlanningEntryCreateDialog, AsiCard, AsiBtn
	}
})
export default class DailyOverview extends Vue {

	@Prop({type: Object, required: true})
	public data!: DailyPlanningData;

	@Prop({type: Boolean, default: false})
	public loading!: boolean;

	@Prop({type: Object, default: null})
	public order!: OrderSimple | null;

	@Prop({type: Boolean, default: true})
	public showCompany!: boolean;

	@Prop({type: Boolean, default: true})
	public showOrder!: boolean;

	@Prop({type: String, default: 'dayMonthLong'})
	public dateFormat!: string;

	private icons = Icon;
	private createDialogOpen = false;
	private entries: PlanningEntryListEntry[] = [];
	private withinOrderRange: boolean = true;

	public created(): void {
		this.prepareEntries();
		this.calculateWithinOrderDateRange();
	}

	private get canCreate(): boolean {
		return this.withinOrderRange && this.$store.getters['user/hasPermission'](Permissions.rightPlanning);
	}

	private get dateString(): string {
		return toISODateString(this.data.date);
	}

	private get isPlanning(): boolean {
		return this.$store.getters['user/hasPermission'](Permissions.rightPlanning);
	}

	@Emit('loadingChanged')
	public loadingChanged(loading: boolean): boolean {
		return loading;
	}

	@Emit('planningEntriesChanged')
	public planningEntriesChanged(): void {
		this.createDialogOpen = false;
	}

	@Watch('data')
	private onDataChanged(): void {
		this.prepareEntries();
	}

	@Watch('order')
	private onOrderChanged(): void {
	    this.calculateWithinOrderDateRange();
	}

	private prepareEntries(): void {
		this.entries = this.data.entries.sort((a: PlanningEntryListEntry, b: PlanningEntryListEntry) => {
			const resCompanyName = a.order.company.name.localeCompare(b.order.name);
			if (resCompanyName !== 0) return resCompanyName;
			const resOrderName = a.order.name.localeCompare(b.order.name);
			if (resOrderName !== 0) return resOrderName;

			if (a.user === null && b.user !== null) return -1;
			if (a.user !== null && b.user === null) return 1;
			if (a.user === null && b.user === null) return 0;

			const fullNameA = UserSimple.fullName(a.user);
			const fullNameB = UserSimple.fullName(b.user);

			if (fullNameA === null && fullNameB === null) return 0;
			if (fullNameA === null) return -1;
			if (fullNameB === null) return 1;
			return fullNameA.localeCompare(fullNameB);
		});
	}

	private createPlanningEntry(): void {
		if (!this.canCreate) return;

		if (!this.order) {
			this.createDialogOpen = true;
		} else {
			const model = new PlanningEntryCreate();
			model.orderId = this.order.id;
			model.date = toISODateString(this.data.date);

			this.loadingChanged(true);
			this.$orderService.createPlanningEntry(model.orderId, model)
				.then(() => {
					this.planningEntriesChanged();
				})
				.catch(() => {
					this.$store.commit('ui/showSnackbar', {
						message: this.$t('snackbar.createError'),
						color: 'error',
					} as SnackbarMessage);
				})
				.finally(() => {
					this.loadingChanged(false);
				});
		}
	}

	private calculateWithinOrderDateRange(): void {
		this.withinOrderRange = true;
		if (this.order) {
			const orderFrom = this.order.dateFrom === null ? null : new Date(this.order.dateFrom);
			if (orderFrom !== null) orderFrom.setHours(0, 0, 0, 0);
			const orderTo = this.order.dateTo === null ? null : new Date(this.order.dateTo);
			if (orderTo !== null) orderTo.setHours(0, 0, 0, 0);
			const currentDay = new Date(this.data.date);
			currentDay.setHours(0, 0, 0, 0);

			if (orderFrom !== null) {
				this.withinOrderRange = this.withinOrderRange && orderFrom <= currentDay;
			}
			if (orderTo !== null) {
				this.withinOrderRange = this.withinOrderRange && orderTo >= currentDay;
			}
		}
	}

}
